/*
 * Beam — restyle Bazaarvoice widgets to follow theme tokens.
 * Rating summary is light DOM; the histogram tooltip portals to <body>;
 * full reviews render in an open shadow root. BV paints gold via !important
 * CSS, inline styles, and SVG attributes — all of those are rewritten here.
 */
(function () {
  'use strict';

  var GOLD = /rgb\(\s*198\s*,\s*158\s*,\s*109\s*\)|#c69e6d/gi;
  var EMPTY = /rgb\(\s*243\s*,\s*243\s*,\s*243\s*\)|#f3f3f3/gi;
  var GOLD_TEST = /rgb\(\s*198\s*,\s*158\s*,\s*109\s*\)|#c69e6d/i;
  var EMPTY_TEST = /rgb\(\s*243\s*,\s*243\s*,\s*243\s*\)|#f3f3f3/i;
  var MISANS_TEST = /MiSans Latin/i;
  var BV_CSS_TEST = /bv_|bazaarvoice|198\s*,\s*158|#c69e6d|MiSans/i;
  var STYLE_ATTR = 'data-beam-bv';
  var DOC_STYLE_ATTR = 'data-beam-bv-doc';
  var WATCHED = 'data-beam-bv-watched';
  var ATTRS = ['stroke', 'fill', 'stop-color', 'color'];

  function schemeHost(el) {
    return (el && el.closest && el.closest('.beam-color-scheme')) || document.body;
  }

  function tokenRgb(host, name, fallback) {
    var raw = getComputedStyle(host).getPropertyValue(name).trim();
    if (!raw) return fallback;
    return 'rgb(' + raw.split(/\s+/).join(', ') + ')';
  }

  function palette(el) {
    var host = schemeHost(el);
    var root = getComputedStyle(document.documentElement);
    var radiusRaw = root.getPropertyValue('--beam-inputs-radius').trim() || '8';
    return {
      star: tokenRgb(host, '--beam-color-heading', 'rgb(22, 24, 29)'),
      empty: tokenRgb(host, '--beam-color-border', 'rgb(224, 227, 232)'),
      text: tokenRgb(host, '--beam-color-text', 'rgb(22, 24, 29)'),
      heading: tokenRgb(host, '--beam-color-heading', 'rgb(22, 24, 29)'),
      font: root.getPropertyValue('--beam-font-body-family').trim() || 'Poppins, sans-serif',
      headingFont: root.getPropertyValue('--beam-font-heading-family').trim() || 'Poppins, sans-serif',
      radius: /px$/.test(radiusRaw) ? radiusRaw : radiusRaw + 'px'
    };
  }

  function tint(value, colors) {
    if (!value) return value;
    return value.replace(GOLD, colors.star).replace(EMPTY, colors.empty);
  }

  function tintFont(value, colors) {
    if (!value || !MISANS_TEST.test(value)) return value;
    return value.replace(/["']?MiSans Latin["']?/gi, colors.font);
  }

  function rewriteStops(root, colors) {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('stop').forEach(function (stop) {
      var style = stop.getAttribute('style') || '';
      if (GOLD_TEST.test(style) || EMPTY_TEST.test(style)) {
        stop.setAttribute('style', tint(style, colors));
      }
      var sc = stop.style.stopColor;
      if (sc && (GOLD_TEST.test(sc) || EMPTY_TEST.test(sc))) {
        stop.style.stopColor = tint(sc, colors);
      }
    });
  }

  function rewriteInline(root, colors) {
    if (!root || !root.querySelectorAll) return;
    var nodes = root.querySelectorAll('[style], [stroke], [fill], [stop-color]');
    var list = [];
    if (root.nodeType === 1) list.push(root);
    for (var i = 0; i < nodes.length; i++) list.push(nodes[i]);
    list.forEach(function (el) {
      if (!el.getAttribute) return;
      var styleAttr = el.getAttribute('style');
      if (styleAttr && (GOLD_TEST.test(styleAttr) || EMPTY_TEST.test(styleAttr) || MISANS_TEST.test(styleAttr))) {
        var next = tintFont(tint(styleAttr, colors), colors);
        if (next !== styleAttr) el.setAttribute('style', next);
      }
      ATTRS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && (GOLD_TEST.test(v) || EMPTY_TEST.test(v))) {
          var nv = tint(v, colors);
          if (nv !== v) el.setAttribute(attr, nv);
        }
      });
    });
  }

  function walkRules(rules, fn) {
    if (!rules) return;
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (rule.cssRules) walkRules(rule.cssRules, fn);
      if (rule.style) fn(rule);
    }
  }

  function rewriteStyleDecl(style, colors) {
    if (!style || !style.length) return;
    var pending = [];
    for (var i = 0; i < style.length; i++) {
      var prop = style[i];
      var v = style.getPropertyValue(prop);
      if (!v) continue;
      if (GOLD_TEST.test(v) || EMPTY_TEST.test(v) || MISANS_TEST.test(v)) {
        pending.push({
          prop: prop,
          value: tintFont(tint(v, colors), colors),
          pri: style.getPropertyPriority(prop) || 'important'
        });
      }
    }
    pending.forEach(function (item) {
      style.setProperty(item.prop, item.value, item.pri);
    });
  }

  function rewriteSheet(sheet, colors) {
    try {
      walkRules(sheet.cssRules, function (rule) {
        rewriteStyleDecl(rule.style, colors);
        var radius = rule.style.borderRadius;
        if (radius === '4px' || radius === '3.6px' || radius === '4.2px') {
          rule.style.setProperty('border-radius', colors.radius, rule.style.getPropertyPriority('border-radius'));
        }
      });
    } catch (e) { /* ignore unreadable sheets */ }
  }

  function rewriteStyleTag(tag, colors) {
    if (!tag || tag.hasAttribute(STYLE_ATTR) || tag.hasAttribute(DOC_STYLE_ATTR)) return;
    var t = tag.textContent;
    if (!t || !BV_CSS_TEST.test(t)) return;
    var next = tintFont(tint(t, colors), colors);
    if (next !== t) tag.textContent = next;
  }

  function rewriteDocumentBvSheets(colors) {
    var sheets = document.styleSheets;
    for (var i = 0; i < sheets.length; i++) {
      var sheet = sheets[i];
      var owner = sheet.ownerNode;
      if (!owner || owner.nodeName !== 'STYLE') continue;
      var text = owner.textContent || '';
      var inBv = owner.closest && owner.closest('[data-bv-show], .bv_modal_component_container');
      if (inBv || BV_CSS_TEST.test(text)) {
        rewriteStyleTag(owner, colors);
        rewriteSheet(sheet, colors);
      }
    }
  }

  function portalCss(colors) {
    return [
      '[data-bv-show="rating_summary"] :is(button, a, [tabindex], [role="button"]):focus,',
      '[data-bv-show="rating_summary"] :is(button, a, [tabindex], [role="button"]):focus-visible,',
      '[data-bv-show="rating_summary"] :is(button, a, [tabindex], [role="button"]):hover {',
      '  outline: none !important;',
      '  outline-offset: 0 !important;',
      '  box-shadow: none !important;',
      '}',
      '.bv_modal_component_container,',
      '.bv_modal_inner_content,',
      '.bv_histogram_row_container,',
      '.bv_button_buttonFull {',
      '  font-family: ' + colors.font + ' !important;',
      '}',
      '.bv_histogram_row_bar_filled {',
      '  background-color: ' + colors.star + ' !important;',
      '  background-image: none !important;',
      '}',
      '.bv_button_buttonFull,',
      'body .bv_button_buttonFull,',
      'html body .bv_button_buttonFull,',
      '[data-bv-show="rating_summary"] .bv_button_buttonFull,',
      '[data-bv-show="rating_summary"] .bv_main_container .bv_button_buttonFull {',
      '  background-color: ' + colors.star + ' !important;',
      '  background-image: none !important;',
      '  color: #fff !important;',
      '  font-weight: 600 !important;',
      '  border-radius: ' + colors.radius + ' !important;',
      '}',
      '.bv_button_buttonFull:hover,',
      'body .bv_button_buttonFull:hover,',
      '[data-bv-show="rating_summary"] .bv_main_container .bv_button_buttonFull:hover {',
      '  background-color: transparent !important;',
      '  color: ' + colors.heading + ' !important;',
      '}'
    ].join('\n');
  }

  function lightCss(colors) {
    return [
      '[data-bv-show="rating_summary"], [data-bv-show="rating_summary"] *:not(svg):not(path):not(polygon):not(stop) {',
      '  font-family: ' + colors.font + ' !important;',
      '}',
      '[data-bv-show="rating_summary"] .bv_avgRating_component_container,',
      '[data-bv-show="rating_summary"] .bv_avgRating_component_container.notranslate,',
      '[data-bv-show="rating_summary"] .bv_numReviews_text,',
      '[data-bv-show="rating_summary"] .bv_numReviews_component_container {',
      '  font-family: ' + colors.font + ' !important;',
      '  font-size: 0.9rem !important;',
      '  font-weight: 600 !important;',
      '  color: ' + colors.heading + ' !important;',
      '}',
      '[data-bv-show="rating_summary"] .bv_button_buttonMinimalist,',
      '[data-bv-show="rating_summary"] .bv_war_button {',
      '  font-size: 0.85rem !important;',
      '  font-weight: 600 !important;',
      '  color: ' + colors.heading + ' !important;',
      '  text-decoration: underline !important;',
      '  text-underline-offset: 0.2em !important;',
      '}'
    ].join('\n');
  }

  function shadowCss(colors) {
    return [
      ':host, :host *, :host *::before, :host *::after {',
      '  font-family: ' + colors.font + ' !important;',
      '}',
      ':host { color: ' + colors.text + '; }',
      'h2, h3, h4 {',
      '  font-family: ' + colors.headingFont + ' !important;',
      '  color: ' + colors.heading + ' !important;',
      '  font-weight: 600 !important;',
      '  letter-spacing: -0.01em !important;',
      '}',
      'h2 { font-size: 1.5rem !important; line-height: 1.25 !important; }',
      'h3 { font-size: 0.8rem !important; text-transform: uppercase !important; letter-spacing: 0.04em !important; }',
      'a { color: ' + colors.heading + ' !important; }',
      'a:hover { text-decoration: underline !important; text-underline-offset: 0.15em !important; }',
      'input, textarea, select {',
      '  font-family: ' + colors.font + ' !important;',
      '  border-radius: ' + colors.radius + ' !important;',
      '  font-size: 0.95rem !important;',
      '}',
      '.badge-label {',
      '  font-weight: 700 !important;',
      '  letter-spacing: 0.04em !important;',
      '  font-size: 0.68rem !important;',
      '}',
      '.bv-reviews-product-rating-star,',
      '.bv-reviews-product-rating-star > div {',
      '  border-color: ' + colors.star + ' !important;',
      '}',
      '.bv-reviews-product-rating-star:hover > div,',
      '.bv-reviews-product-rating-star:focus-within > div,',
      '.bv-reviews-product-rating-star > div[style*="background"] {',
      '  background-color: ' + colors.star + ' !important;',
      '  border-color: ' + colors.star + ' !important;',
      '}',
      '.bv-reviews-product-rating-star svg g[stroke],',
      '.bv-reviews-product-rating-star svg path {',
      '  stroke: ' + colors.star + ' !important;',
      '}',
      '.bv-reviews-product-rating-star:hover svg path,',
      '.bv-reviews-product-rating-star:focus-within svg path,',
      '.bv-reviews-product-rating-star > div[style*="background"] svg path,',
      '.bv-reviews-product-rating-star > div[style*="background"] svg g[stroke] {',
      '  stroke: #fff !important;',
      '}'
    ].join('\n');
  }

  function ensureStyle(root, css, attr) {
    attr = attr || STYLE_ATTR;
    var node = root.querySelector && root.querySelector('style[' + attr + ']');
    if (!node) {
      node = document.createElement('style');
      node.setAttribute(attr, '');
    }
    var alreadyLast = root.lastElementChild === node;
    if (node.textContent === css && alreadyLast) return;
    if (node.textContent !== css) node.textContent = css;
    if (!alreadyLast) (root.head || root).appendChild(node);
  }

  function restyleRoot(root, colors, isShadow) {
    if (!root) return;
    ensureStyle(root, isShadow ? shadowCss(colors) : lightCss(colors));
    if (root.querySelectorAll) {
      root.querySelectorAll('style').forEach(function (tag) { rewriteStyleTag(tag, colors); });
    }
    if (root.styleSheets) {
      for (var i = 0; i < root.styleSheets.length; i++) rewriteSheet(root.styleSheets[i], colors);
    }
    rewriteStops(root, colors);
    rewriteInline(root, colors);
  }

  function observe(root, onChange) {
    if (!root || root[WATCHED]) return;
    try {
      Object.defineProperty(root, WATCHED, { value: true, configurable: true });
    } catch (e) {
      root[WATCHED] = true;
    }
    if (!('MutationObserver' in window)) return;
    new MutationObserver(onChange).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'stroke', 'fill', 'stop-color']
    });
  }

  function restyle(el) {
    if (!el || el.nodeType !== 1 || !el.hasAttribute('data-bv-show')) return;
    var colors = palette(el);
    restyleRoot(el, colors, false);
    observe(el, function () { restyle(el); });
    if (el.shadowRoot) {
      restyleRoot(el.shadowRoot, colors, true);
      observe(el.shadowRoot, function () { restyle(el); });
    }
  }

  function restylePortal(el) {
    if (!el || el.nodeType !== 1) return;
    var colors = palette(el);
    rewriteStops(el, colors);
    rewriteInline(el, colors);
    observe(el, function () { restylePortal(el); });
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-bv-show]').forEach(restyle);
    document.querySelectorAll('.bv_modal_component_container, #bv_components_histogram').forEach(restylePortal);
    var sample = document.querySelector('[data-bv-show], .beam-color-scheme, body');
    var colors = palette(sample);
    ensureStyle(document.body, portalCss(colors), DOC_STYLE_ATTR);
    rewriteDocumentBvSheets(colors);
  }

  scan(document);

  var ticks = 0;
  var iv = setInterval(function () {
    scan(document);
    ticks += 1;
    if (ticks > 40) clearInterval(iv);
  }, 250);

  if ('MutationObserver' in window) {
    new MutationObserver(function () { scan(document); }).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('shopify:section:load', function () { scan(document); });
  var hoverTimer;
  document.addEventListener('mouseover', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('[data-bv-show], .bv_modal_component_container')) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { scan(document); }, 40);
  }, true);
})();
